import { Form } from 'react-bootstrap';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BSL_LEVELS } from '../../constants/sample.constants';
import {
  SAMPLE_TYPE_CODE_ORDER,
  formatSampleTypeDisplay,
} from '../../utils/sampleTypeLabels';
import type { SampleFormData } from '../../utils/sampleFormSchema';
import { SAMPLE_UNIT_OPTIONS } from '../../utils/sampleUnitLabels';
import TextContentSep from '../common/TextContentSep';
import BaseDateTimeField from './BaseDateTimeField';
import { SampleFormSectionTitle } from './SampleFormSectionTitle';

export type SampleFormBasicFieldsProps = {
  register: UseFormRegister<SampleFormData>;
  errors: FieldErrors<SampleFormData>;
  isEditMode: boolean;
  creatorDisplayName: string;
  sampleCreatedDate: string;
  onSampleCreatedDateChange: (value: string) => void;
  /** 現在の種別（正規コード外の保存値を option に残すため） */
  sampleTypeValue: string;
};

/** サンプル登録・編集の基本情報（名前・タイプ・BSL・本数・作製日） */
export default function SampleFormBasicFields({
  register,
  errors,
  isEditMode,
  creatorDisplayName,
  sampleCreatedDate,
  onSampleCreatedDateChange,
  sampleTypeValue,
}: SampleFormBasicFieldsProps) {
  const { t, i18n } = useTranslation();
  const hasLegacySampleType =
    !!sampleTypeValue &&
    !(SAMPLE_TYPE_CODE_ORDER as readonly string[]).includes(sampleTypeValue);

  return (
    <div className="sample-form-section">
      <SampleFormSectionTitle icon="fa-info-circle">
        {t('sample.basic_information')}
      </SampleFormSectionTitle>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="sample-name" className="fw-semibold">
          {t('sample.field_sample_name_label')} <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          id="sample-name"
          name="name"
          type="text"
          {...register('name')}
          isInvalid={!!errors.name}
          autoComplete="off"
          autoFocus={!isEditMode}
          placeholder={t('sample.field_sample_name_placeholder')}
        />
        <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
        <TextContentSep />
      </Form.Group>

      {/* xs: 縦積み（col-6 だと「病原体（ストック）」等が切れる）。sm+ で横並び */}
      <div className="row">
        <Form.Group className="mb-3 col-12 col-sm-6 col-md-4">
          <Form.Label htmlFor="sample-type" className="fw-semibold">
            {t('sample.sample_type')}
          </Form.Label>
          <Form.Select
            id="sample-type"
            name="sampleType"
            {...register('sampleType')}
            autoComplete="off"
          >
            <option value="">{t('common.select')}</option>
            {hasLegacySampleType ? (
              <option value={sampleTypeValue}>
                {formatSampleTypeDisplay(sampleTypeValue, t)}
              </option>
            ) : null}
            {SAMPLE_TYPE_CODE_ORDER.map((code) => (
              <option key={code} value={code}>
                {t(`sample.sample_types.${code}`)}
              </option>
            ))}
          </Form.Select>
          <TextContentSep />
        </Form.Group>

        <Form.Group className="mb-3 col-12 col-sm-6 col-md-4">
          <Form.Label
            htmlFor="sample-bsl-level"
            className="fw-semibold"
            title={t('sample.bsl_level')}
          >
            {t('sample.bsl_level_short', { defaultValue: 'BSL' })}
          </Form.Label>
          <Form.Select
            id="sample-bsl-level"
            name="bslLevel"
            {...register('bslLevel')}
            autoComplete="off"
          >
            <option value="">{t('common.select')}</option>
            {BSL_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </Form.Select>
          <TextContentSep />
        </Form.Group>

        {/* 作製者は自動設定のみ。スマホでは縦を節約して非表示 */}
        <Form.Group className="mb-3 col-md-4 d-none d-md-block">
          <Form.Label htmlFor="sample-maker-name" title={t('sample.maker_name')}>
            {t('sample.creator', { defaultValue: '作製者' })}
          </Form.Label>
          <Form.Control
            id="sample-maker-name"
            name="makerName"
            type="text"
            value={creatorDisplayName}
            readOnly
            autoComplete="off"
            className="bg-light"
          />
          <Form.Text className="text-muted small">
            <i className="fas fa-info-circle me-1" aria-hidden="true" />
            {t('sample.maker_auto_set_warning')}
          </Form.Text>
          <TextContentSep />
        </Form.Group>
      </div>

      {!isEditMode ? (
        /* xs: datetime-local が col-7 に潰れるので縦積み。md+ で横並び */
        <div className="row mb-3">
          <div className="col-12 col-md-6 mb-3 mb-md-0">
            <Form.Group className="mb-0">
              <Form.Label htmlFor="sample-initial-quantity" className="fw-semibold">
                {t('sample.registration_quantity')} <span className="text-danger">*</span>
              </Form.Label>
              <div className="input-group">
                <Form.Control
                  id="sample-initial-quantity"
                  name="initialQuantity"
                  type="number"
                  min="0"
                  {...register('initialQuantity', {
                    valueAsNumber: true,
                    required: t('sample.registration_quantity_required'),
                    validate: (value) => {
                      if (value === null || value === undefined) {
                        return t('sample.registration_quantity_required');
                      }
                      if (typeof value === 'number' && value < 0) {
                        return t('sample.registration_quantity_invalid');
                      }
                      return true;
                    },
                  })}
                  isInvalid={!!errors.initialQuantity}
                  autoComplete="off"
                  placeholder={t('sample.registration_quantity_placeholder')}
                />
                <Form.Select
                  id="sample-unit"
                  aria-label={t('sample.unit')}
                  className="sample-form-unit-select"
                  {...register('unit')}
                >
                  {SAMPLE_UNIT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <Form.Control.Feedback type="invalid">
                {errors.initialQuantity?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted d-none d-md-block">
                {t('sample.registration_quantity_note')}
              </Form.Text>
            </Form.Group>
          </div>
          <div className="col-12 col-md-6">
            <Form.Group className="mb-0">
              <BaseDateTimeField
                id="sample-created-date"
                name="sampleCreatedDate"
                label={t('sample.sample_created_date')}
                value={sampleCreatedDate}
                onChange={onSampleCreatedDateChange}
                lang={i18n.language}
                className="sample-form-datetime"
              />
              <Form.Text className="text-muted small d-none d-md-block">
                {t('sample.sample_created_date_note')}
              </Form.Text>
            </Form.Group>
          </div>
        </div>
      ) : (
        <Form.Group className="mb-3">
          <Form.Label htmlFor="sample-unit" className="fw-semibold">
            {t('sample.unit')}
          </Form.Label>
          <Form.Select
            id="sample-unit"
            className="sample-form-unit-select"
            {...register('unit')}
          >
            {SAMPLE_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}
    </div>
  );
}
